import { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import Constellation from "./Constellation";
import AuthorWordmark from "./AuthorWordmark";
import SocialLink from "./SocialLink";
import AnalyticsConsent from "./AnalyticsConsent";
import GoodreadsLink from "./GoodreadsLink";
import TrilogyNavigation from "./TrilogyNavigation";
import FooterSignup from "./FooterSignup";
import { trackEvent, trackPageView } from "../lib/telemetry";

export default function SiteLayout() {
  const location = useLocation();
  const normalisedPath = location.pathname.replace(/\/$/, "") || "/";
  const lightHeader = ["/about", "/privacy"].includes(normalisedPath) || ![
    "/",
    "/books",
    "/books/the-universe-without-noise",
    "/books/the-paradox-beyond-silence",
    "/books/the-forever-inside-nothing",
    "/links",
    "/the-universe-of-the-optimiser-trilogy",
  ].includes(normalisedPath);
  const pageTheme = {
    "/books/the-paradox-beyond-silence": "site-shell--ember",
    "/books/the-forever-inside-nothing": "site-shell--verdant",
  }[normalisedPath] || "";
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const menuButtonRef = useRef(null);
  const navigationRef = useRef(null);

  useEffect(() => {
    setMenuOpen(false);
    trackPageView(location.pathname, location.search);
    trackEvent("Conversion", {
      "Conversion Type": "page_view",
      "Conversion Value": 0,
    });

    let scrollFrame = 0;
    let finished = false;
    const main = document.getElementById("main-content");

    const applyScroll = () => {
      if (finished) return;
      let hashId;
      try {
        hashId = decodeURIComponent(location.hash.slice(1));
      } catch {
        hashId = location.hash.slice(1);
      }
      const target = hashId ? document.getElementById(hashId) : main?.querySelector("h1");
      if (!target) return;

      finished = true;
      observer.disconnect();
      const focusTarget = target.matches("h1, h2, h3") ? target : target.querySelector("h1, h2, h3") || target;
      // Keep citation links in the keyboard order when returning from sources.
      if (!focusTarget.matches("a[href], button, input, select, textarea, [tabindex]")) {
        focusTarget.setAttribute("tabindex", "-1");
      }
      if (!focusTarget.matches("a[href], button, input, select, textarea")) {
        focusTarget.classList.add("route-focus-target");
      }
      focusTarget.focus({ preventScroll: true });
      const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (hashId) {
        target.scrollIntoView({ behavior: reducedMotion ? "instant" : "smooth", block: "start" });
      } else {
        window.scrollTo({ top: 0, left: 0, behavior: "instant" });
      }
    };

    // Lazy pages can mount after the route effect. Watch for the real target.
    const observer = new MutationObserver(() => {
      window.cancelAnimationFrame(scrollFrame);
      scrollFrame = window.requestAnimationFrame(applyScroll);
    });
    if (main) observer.observe(main, { childList: true, subtree: true });
    scrollFrame = window.requestAnimationFrame(applyScroll);

    return () => {
      observer.disconnect();
      window.cancelAnimationFrame(scrollFrame);
    };
  }, [location.hash, location.pathname, location.search, location.key]);

  useEffect(() => {
    const updateProgress = () => {
      const root = document.documentElement;
      const available = root.scrollHeight - root.clientHeight;
      setScrollProgress(available > 0 ? root.scrollTop / available : 0);
    };

    updateProgress();
    window.addEventListener("scroll", updateProgress, { passive: true });
    window.addEventListener("resize", updateProgress);
    return () => {
      window.removeEventListener("scroll", updateProgress);
      window.removeEventListener("resize", updateProgress);
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) {
      return undefined;
    }

    const navigation = navigationRef.current;
    const menuButton = menuButtonRef.current;
    // Recalculate after a submenu expands so hidden links never enter the trap.
    const getNavigationControls = () => Array.from(navigation?.querySelectorAll("a[href], button:not([disabled])") || [])
      .filter((element) => element.getClientRects().length > 0 && window.getComputedStyle(element).visibility !== "hidden");

    getNavigationControls()[0]?.focus();

    function handleMenuKeydown(event) {
      if (event.key === "Escape") {
        event.preventDefault();
        setMenuOpen(false);
        window.requestAnimationFrame(() => menuButton?.focus());
        return;
      }

      const focusableElements = [menuButton, ...getNavigationControls()].filter(Boolean);
      if (event.key !== "Tab" || focusableElements.length === 0) {
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }

    document.addEventListener("keydown", handleMenuKeydown);
    return () => document.removeEventListener("keydown", handleMenuKeydown);
  }, [menuOpen]);

  return (
    <div className={`site-shell ${lightHeader ? "site-shell--light-header" : ""} ${pageTheme}`.trim()}>
      <div className="scroll-progress" aria-hidden="true">
        <span style={{ transform: `scaleX(${scrollProgress})` }} />
      </div>
      <a className="skip-link" href="#main-content">
        Skip to content
      </a>
      <header className="site-header">
        <Link className="wordmark" to="/" aria-label="R A Hewitt, home">
          <AuthorWordmark />
        </Link>
        <button
          ref={menuButtonRef}
          className="menu-toggle"
          type="button"
          aria-expanded={menuOpen}
          aria-controls="site-navigation"
          onClick={() => setMenuOpen((current) => !current)}
        >
          <span className="sr-only">{menuOpen ? "Close navigation" : "Open navigation"}</span>
          <span />
          <span />
        </button>
        <nav ref={navigationRef} id="site-navigation" className={`site-nav ${menuOpen ? "is-open" : ""}`} aria-label="Primary navigation">
          <TrilogyNavigation mobileMenuOpen={menuOpen} />
          <NavLink to="/books/the-universe-without-noise#chapter-one">Read chapter one</NavLink>
          <NavLink to="/about">About the author</NavLink>
          <NavLink to="/musings">Musings</NavLink>
          <NavLink to="/links">Elsewhere</NavLink>
          <Link className="site-nav__purchase" to="/books">
            Browse editions <span aria-hidden="true">→</span>
          </Link>
        </nav>
        <Constellation className="site-constellation" />
      </header>
      <main id="main-content">
        <Outlet />
      </main>
      <FooterSignup key={normalisedPath} />
      <footer className="site-footer">
        <div>
          <Link className="wordmark wordmark--footer" to="/" aria-label="R A Hewitt, home">
            <AuthorWordmark />
          </Link>
        </div>
        <nav className="site-footer__links" aria-label="Footer navigation">
          <Link to="/books/the-universe-without-noise">Book One</Link>
          <Link to="/books/the-paradox-beyond-silence">Book Two</Link>
          <Link to="/books/the-forever-inside-nothing">Book Three</Link>
          <Link to="/about">About</Link>
          <Link to="/musings">Musings</Link>
          <Link to="/books">Browse editions</Link>
        </nav>
        <p className="site-footer__tagline">Science fiction about perfect systems and imperfect people.</p>
        <div className="site-footer__bottom">
          <p className="site-footer__legal">© 2026 Ross A Hewitt. All rights reserved.</p>
          <nav className="site-footer__utilities" aria-label="Footer resources and preferences">
            <Link to="/links">Links</Link>
            <SocialLink className="footer-link" source="site-footer">Follow on X <span aria-hidden="true">↗</span></SocialLink>
            <GoodreadsLink className="footer-link" source="site-footer">Goodreads</GoodreadsLink>
            <a href="mailto:press@rahewitt.com">Contact</a>
            <Link to="/privacy">Privacy</Link>
            <button className="site-footer__analytics" type="button" onClick={() => window.dispatchEvent(new Event("open-analytics-settings"))}>Analytics choices</button>
          </nav>
        </div>
      </footer>
      <AnalyticsConsent />
    </div>
  );
}
